import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Button, Card, Group, ScrollArea, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSend, IconSparkles, IconTrash, IconZoomCheck } from '@tabler/icons-react';
import { useLiveQuery } from 'dexie-react-hooks';
import { redactor, type Ballot, type BallotFields, type Round } from '@flow/core';
import { run, useDeps, useUseCases } from '../../app/services';
import { ApiError } from '../../infra/api';
import { useAiAvailable } from './useAi';
import { useBallotAssistant, type AssistantEdits, type AssistantMode } from './useDecisionAi';

const why = (e: unknown) =>
  e instanceof ApiError && e.body && typeof e.body === 'object' && 'message' in e.body ? String(e.body.message) : "The assistant didn't answer. Try again.";

/**
 * Right column of the Ballot tab: draft → you edit → critique, plus free-form requests ("shorter", "kinder to [name]").
 * It sees the form fields as they are at each turn. It can't change the decision.
 */
export const BallotAssistant = ({ round, ballot, fields, onEdits }: { round: Round; ballot: Ballot; fields: BallotFields; onEdits: (e: AssistantEdits) => void }) => {
  const deps = useDeps();
  const uc = useUseCases();
  const ai = useAiAvailable();
  const ask = useBallotAssistant();
  const names = useLiveQuery(() => deps.names.get(round.id), [round.id]);
  const [busy, setBusy] = useState<AssistantMode | null>(null);
  const [input, setInput] = useState('');
  const viewport = useRef<HTMLDivElement>(null);
  const chat = ballot.assistantChat ?? [];
  const show = redactor(round, names).restoreNames;

  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
  }, [chat.length, busy]);

  const send = async (mode: AssistantMode, message?: string) => {
    const hasText = fields.rfd.trim() || fields.teamComments.A.trim() || fields.teamComments.B.trim() || Object.values(fields.speakerComments).some((t) => t.trim());
    if (mode === 'draft' && hasText && !confirm('Replace what you have written with a new draft?')) return;
    setBusy(mode);
    try {
      onEdits(await ask(round, ballot, fields, mode, message));
      if (mode === 'chat') setInput('');
    } catch (e) {
      notifications.show({ color: 'yellow', title: 'Assistant', message: why(e) });
    } finally {
      setBusy(null);
    }
  };

  if (!ai) {
    return (
      <Card className="panel" padding="sm">
        <Text fw={600} size="sm" mb={4}>Ballot assistant</Text>
        <Text size="xs" c="dimmed">Sign in (Settings) and be online to get a first draft, changes on request, and a critique.</Text>
      </Card>
    );
  }

  return (
    <Card className="panel" padding="sm" data-testid="ballot-assistant">
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="sm">Ballot assistant</Text>
        {chat.length > 0 && (
          <Tooltip label="Clear the conversation">
            <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => void run(uc.saveBallot(round.id, { assistantChat: [] }))} aria-label="Clear the conversation"><IconTrash size={14} /></ActionIcon>
          </Tooltip>
        )}
      </Group>
      <Group gap={6} mb="xs" grow>
        <Button size="xs" leftSection={<IconSparkles size={14} />} loading={busy === 'draft'} disabled={!!busy} onClick={() => void send('draft')} data-testid="assistant-draft">Draft</Button>
        <Button size="xs" variant="default" leftSection={<IconZoomCheck size={14} />} loading={busy === 'critique'} disabled={!!busy} onClick={() => void send('critique')} data-testid="assistant-critique">Critique</Button>
      </Group>
      <ScrollArea.Autosize mah={360} viewportRef={viewport} type="auto">
        <Stack gap={6}>
          {chat.length === 0 && <Text size="xs" c="dimmed">Start with a draft, edit it in your words, then ask for a critique. Or ask for changes: "shorter", "kinder to the first speaker", "mention the dropped turn".</Text>}
          {chat.map((m, i) => (
            <Box
              key={i}
              p={6}
              style={{ alignSelf: m.role === 'judge' ? 'flex-end' : 'flex-start', maxWidth: '92%', borderRadius: 6, background: m.role === 'judge' ? 'var(--mantine-primary-color-light)' : 'var(--mantine-color-default-hover)' }}
              data-testid={`chat-${m.role}`}
            >
              <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{show(m.text)}</Text>
            </Box>
          ))}
          {busy && <Text size="xs" c="dimmed">Thinking…</Text>}
        </Stack>
      </ScrollArea.Autosize>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (input.trim() && !busy) void send('chat', input);
        }}
      >
        <Group gap={4} mt="xs" wrap="nowrap" align="flex-end">
          <Textarea
            style={{ flex: 1 }}
            autosize
            minRows={1}
            maxRows={4}
            size="xs"
            placeholder="Ask for a change…"
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (input.trim() && !busy) void send('chat', input);
              }
            }}
            aria-label="Message the ballot assistant"
          />
          <ActionIcon type="submit" size="lg" variant="filled" loading={busy === 'chat'} disabled={!input.trim() || !!busy} aria-label="Send"><IconSend size={16} /></ActionIcon>
        </Group>
      </form>
      <Text size="xs" c="dimmed" mt={6}>Sends your flow, calls, notes and tags to Claude, with names removed. It can't change your decision.</Text>
    </Card>
  );
};
